import type { FormField } from '../types';
import type { RespondentField } from './multimodal-contract';
import { validateQuestionImage } from './question-blocks';

/** KLIPY's documented direct media hosts. */
function isKlipyMediaUrl(value: string | undefined): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && /^(?:static|static1|static2)\.klipy\.com$/i.test(url.hostname);
  } catch {
    return false;
  }
}

/** Projects authored settings into the real respondent UI without private metadata. */
export function projectPreviewField(field: FormField, fields: readonly FormField[]): RespondentField {
  const projected: RespondentField = {
    fieldId: field.id, type: field.type, label: field.label, required: field.required,
    answerChannel: field.answerChannel ?? 'any',
    ...(field.helpText ? { helpText: field.helpText } : {}),
    ...(field.placeholder ? { placeholder: field.placeholder } : {}),
  };
  if (field.type === 'file_upload') { projected.fileConstraints = field.fileConstraints; projected.answerChannel = 'on_screen'; }
  if (['checkbox', 'image_choice', 'ranking', 'date_time', 'matrix', 'address', 'signature', 'image_click_map'].includes(field.type)) {
    projected.answerChannel = 'on_screen';
  }
  if (field.type === 'short_text') {
    projected.minLength = field.minLength;
    projected.maxLength = field.maxLength;
    projected.textPresentation = field.textPresentation;
    projected.textFormat = field.textFormat;
  }
  if (field.type === 'number') {
    projected.min = field.min; projected.max = field.max; projected.step = field.step;
  }
  if (field.type === 'choice_multi') {
    projected.minSelected = field.minSelected; projected.maxSelected = field.maxSelected;
  }
  if (field.type === 'checkbox' && field.checkbox) projected.checkbox = structuredClone(field.checkbox);
  if (field.type === 'image_choice') {
    projected.selectionMode = field.selectionMode;
    projected.minSelected = field.minSelected;
    projected.maxSelected = field.maxSelected;
    projected.options = field.options.map(({ id, label, image }) => ({ id, label,
      ...(image && validateQuestionImage(image).length === 0 ? { image: { imageUrl: image.imageUrl, alt: image.alt } } : {}) }));
  }
  if (field.type === 'ranking') projected.options = field.options.map(({ id, label }) => ({ id, label }));
  if (field.type === 'date_time') projected.dateTime = structuredClone(field.dateTime);
  if (field.type === 'matrix') {
    projected.matrix = structuredClone(field.matrix);
    if (field.selectionMode !== undefined) projected.selectionMode = field.selectionMode;
  }
  if (field.type === 'address') projected.address = structuredClone(field.address);
  if (field.type === 'image_click_map') {
    projected.minClicks = field.minClicks;
    projected.maxClicks = field.maxClicks;
    if (field.image && validateQuestionImage(field.image).length === 0) projected.image = { imageUrl: field.image.imageUrl, alt: field.image.alt };
  }
  if (field.type === 'choice_multi' || field.type === 'choice_single') {
    if (field.otherOptionId !== undefined) projected.otherOptionId = field.otherOptionId;
    if (field.choicePresentation !== undefined) projected.choicePresentation = field.choicePresentation;
    projected.options = field.options.map(({ id, label }) => ({ id, label }));
  }
  if ((field.type === 'number' || field.type === 'choice_single') && field.rating) {
    projected.rating = { version: 1, preset: field.rating.preset, direction: field.rating.direction,
      lowLabel: field.rating.lowLabel, highLabel: field.rating.highLabel };
  }
  const information = field.type === 'information' ? field
    : fields.find(candidate => candidate.id === field.informationFieldId);
  if (information?.type === 'information') {
    const uploadedImageUrl = information.imageUrl?.startsWith('/api/workspace-assets/') ? information.imageUrl : undefined;
    const klipyImageUrl = information.imageSource === 'klipy' && isKlipyMediaUrl(information.imageUrl)
      ? information.imageUrl : undefined;
    const imageUrl = uploadedImageUrl ?? klipyImageUrl;
    projected.information = {
      fieldId: information.id, title: information.title ?? information.label,
      passive: information.responseMode !== 'legacy_reaction', delivery: information.delivery ?? 'spoken',
      canContinue: field.id === information.id && information.responseMode !== 'legacy_reaction',
      ...(information.body ? { body: information.body } : {}),
      ...(imageUrl ? { imageUrl } : {}),
      ...(klipyImageUrl && isKlipyMediaUrl(information.imageStillUrl)
        ? { imageStillUrl: information.imageStillUrl, imageSource: 'klipy' as const }
        : {}),
    };
  }
  return projected;
}
